import { css, html } from 'lit';

import { BaseAtomElement } from './base/BaseAtomElement.js';

const PARSE_INT_BASE = 10;

/**
 *  Skeleton for text element.
 *
 */
export class VeneerSkeletonText extends BaseAtomElement {
  declare rows?: number;

  declare gutter?: string;

  declare randomWidth: boolean;

  private declare _randomWidthList: Array<string>;

  static override properties = {
    /**
     * Sets the number of text rows.
     */
    rows: { type: Number, reflect: true },
    /**
     * Sets the space between the text rows.
     */
    gutter: { type: String },
    /**
     * If `true`, applies a random width to each text row for a natural paragraph appearance.
     */
    randomWidth: { type: Boolean, attribute: 'random-width', reflect: true },
    /**
     * Sets text rows' height.
     */
    height: { type: String },
    /**
     * Sets text rows' width.
     */
    width: { type: String },
    _randomWidthList: { attribute: false, state: true },
  };

  constructor() {
    super();
    this.rows = 1;
    this.gutter = '8px';
    this.height = '20px';
    this.width = '100%';
    this.randomWidth = false;
    this._randomWidthList = [];
  }

  override attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value);
    if (name === 'rows' && value) {
      const parsedValue = parseInt(value, PARSE_INT_BASE);
      if (parsedValue > this._randomWidthList.length) {
        this.addRandomWidth(parsedValue - this._randomWidthList.length);
      } else if (parsedValue < this._randomWidthList.length) {
        this.removeRandomWidth(this._randomWidthList.length - parsedValue);
      }
    }
  }

  override updated(
    changedProperties: Map<string | number | symbol, unknown>,
  ): void {
    super.updated(changedProperties);
    if (changedProperties.has('_randomWidthList')) {
      if (this.randomWidth) {
        this.requestUpdate();
      }
    }
  }

  /**
   * Adds random width values to the random width array
   */
  private addRandomWidth(rowsToAdd: number = 1) {
    const minLineMultiplier = 0.2;
    const maxLineMultiplier = 1.0;
    const additionalValues = [];
    for (let i = 0; i < rowsToAdd; i += 1) {
      const newWidth =
        Math.random() * (maxLineMultiplier - minLineMultiplier) +
        minLineMultiplier;

      additionalValues.push(`${newWidth * 100}%`);
    }
    this._randomWidthList = this._randomWidthList.concat(additionalValues);
  }

  /**
   * Removes random width values from the random width array
   */
  private removeRandomWidth(rowsToRemove: number = 1) {
    if (this._randomWidthList.length === 0) {
      return;
    }
    const listCopy = [...this._randomWidthList];

    for (let i = 0; i < rowsToRemove; i += 1) {
      listCopy.pop();
    }
    this._randomWidthList = listCopy;
  }

  override render() {
    const lastLineWidthMultiplier = 0.6;

    const renderRows = this.rows ?? 1;
    const renderWidth = this.width ?? '100%';

    function getNormalizedRenderWidth(index: number) {
      return index === renderRows - 1 && renderRows > 1
        ? `calc(${renderWidth}*${lastLineWidthMultiplier})`
        : renderWidth;
    }

    const renderedRows = Array.from({ length: renderRows }, (_, index) => {
      const normalizedWidth = this.randomWidth
        ? this._randomWidthList[index]
        : getNormalizedRenderWidth(index);

      return html`<div
        class="vn-skel-loading__text"
        style="
          --vn-skeleton-text-width: ${normalizedWidth};
          --vn-skeleton-text-height: ${this.height};
          --vn-skeleton-text-spacing: ${this.gutter};
        "
      ></div>`;
    });

    return html`${renderedRows}<slot></slot>`;
  }

  static override styles = [
    ...super.styles,
    css`
      :host {
        --vn-skeleton-foreground-color: var(
          --vn-skeleton-effect-foreground-color__text
        );
        --vn-skeleton-background-color: var(
          --vn-skeleton-effect-background-color__text
        );
      }
      :host > div {
        width: var(--vn-skeleton-text-width);
        height: var(--vn-skeleton-text-height);
      }
      :host([theme-shape='round']) > div {
        border-radius: 4px;
      }
      :host([theme-shape='sharp']) > div {
        border-radius: 0px;
      }
      :host([theme-shape='pill']) > div {
        border-radius: 100vh;
      }
      :host > div:not(:last-child) {
        margin-bottom: var(--vn-skeleton-text-spacing);
      }
    `,
  ];
}

if (!customElements.get('veneer-skeleton-text')) {
  customElements.define('veneer-skeleton-text', VeneerSkeletonText);
}

declare global {
  interface HTMLElementTagNameMap {
    'veneer-skeleton-text': VeneerSkeletonText;
  }
}
